import { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../api';
import { getDashboardBuilderSteps } from './tracks/dashboardBuilderTour';

const TOUR_KEY = 'dashboard_builder_v1';
const LOCAL_CACHE_PREFIX = 'aidas_tour_state_';

export const useDashboardTour = (userId, token) => {
  const [run, setRun] = useState(false);
  const [tourServerState, setTourServerState] = useState({});
  const hasChecked = useRef(false);

  // Memoize steps once
  const [steps] = useState(() => getDashboardBuilderSteps());

  // ── Check tour state on mount ────────────────────────────────────────────
  useEffect(() => {
    if (!userId || hasChecked.current) return;
    hasChecked.current = true;

    const localKey = `${LOCAL_CACHE_PREFIX}${userId}`;

    // Fast path: check localStorage first to avoid API round-trip on re-visits
    try {
      const cached = localStorage.getItem(localKey);
      if (cached) {
        const state = JSON.parse(cached);
        if (state[TOUR_KEY]) return; // Already seen — do nothing
      }
    } catch (e) {
      // Corrupted cache — fall through to API
    }

    // Slow path: fetch from server
    const fetchState = async () => {
      try {
        const serverState = await apiFetch('/users/me/tour-state', token);
        const state = serverState || {};
        setTourServerState(state);

        // Write to localStorage so subsequent visits skip the API call
        try {
          localStorage.setItem(localKey, JSON.stringify(state));
        } catch (e) {}

        if (!state[TOUR_KEY]) {
          // Delay ensures the page has fully rendered before the tour starts
          setTimeout(() => setRun(true), 1400);
        }
      } catch (e) {
        // If API fails and there's no local cache, still show the tour
        try {
          const cached = localStorage.getItem(localKey);
          if (!cached) setTimeout(() => setRun(true), 1400);
        } catch {}
      }
    };

    fetchState();
  }, [userId, token]);

  // ── Mark tour as seen (called by TourEngine on finish or skip) ───────────
  const markSeen = useCallback(async () => {
    setRun(false);
    if (!userId) return;

    const localKey = `${LOCAL_CACHE_PREFIX}${userId}`;
    const newState = { ...tourServerState, [TOUR_KEY]: true };
    setTourServerState(newState);

    // Optimistic localStorage update
    try {
      localStorage.setItem(localKey, JSON.stringify(newState));
    } catch (e) {}

    // Persist to server
    try {
      await apiFetch('/users/me/tour-state', token, {
        method: 'POST',
        body: JSON.stringify({ [TOUR_KEY]: true }),
      });
    } catch (e) {
      console.error('[useDashboardTour] Failed to persist tour state to server:', e);
    }
  }, [tourServerState, userId, token]);

  // ── Allow restarting the tour ──────────────────
  const restartTour = useCallback(() => {
    setRun(true);
  }, []);

  return { steps, run, setRun, markSeen, restartTour };
};
